import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { ArrowLeft, Download } from 'lucide-react';
import { useLegalAgb, AGB_PDF_URL } from '../components/LegalTexts';
import { useTranslation } from 'react-i18next';

const SECTION_HEADING = 'font-serif text-2xl text-[#1B3A5C] mt-10 mb-3';
const BODY = 'text-[#34536b] text-sm leading-relaxed';

const AGB = () => {
  const { t, i18n } = useTranslation();
  const [forceDe, setForceDe] = useState(false);
  const agb = useLegalAgb(forceDe ? 'de' : undefined);
  const isTranslation = Boolean(agb?.notice) && agb?.lang !== 'de';
  const siteLangIsDe = (i18n.language || 'de').startsWith('de');
  const AGB_SECTIONS = agb?.sections || [];
  const AGB_WIDERRUF = agb?.widerruf;
  const AGB_KOPF = agb?.kopf || {};
  const AGB_STAND = agb?.stand || '';
  return (
  <div className="min-h-screen bg-white">
    <Seo
      title="Allgemeine Mandatsbedingungen (AGB) – Kanzlei Nabhan Gäbler & Partner"
      description="Allgemeine Mandatsbedingungen (AGB) der Kanzlei Nabhan Gäbler & Partner, Bonn – Geltungsbereich, Erstberatung, Vergütung, Haftung, Widerrufsbelehrung."
      path="/agb"
    />
    <Header />
    <main className="pt-[150px]">
      <section className="bg-[#1B3A5C] text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#b8893d] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-1 text-white/70 text-sm hover:text-[#b8893d] mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('common.back_home', 'Zurück zur Startseite')}
          </Link>
          <div className="inline-flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-3">
            <span className="w-12 h-px bg-[#b8893d]" />
            {t('legal_pages.agb.kicker')}
          </div>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight">{t('legal_pages.agb.title')}</h1>
          <p className="text-white/70 text-xs md:text-sm mt-4 leading-relaxed" data-testid="agb-kopf">
            {AGB_KOPF.firma} · {AGB_KOPF.adresse} · {AGB_KOPF.register} · Telefon {AGB_KOPF.telefon} · {AGB_KOPF.email} · Stand: {AGB_STAND}
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10" data-testid="agb-content">
          {isTranslation && (
            <div className="mb-8 p-4 bg-[#fbf7f0] border-l-4 border-[#b8893d] text-sm text-[#34536b] leading-relaxed" data-testid="agb-translation-notice">
              <p>{agb.notice}</p>
              <p className="mt-2 text-xs">
                <span className="text-[#7a8a99]">DE:</span> Unverbindliche Übersetzung – rechtlich maßgeblich ist ausschließlich die deutsche Fassung.{' '}
                <button type="button" onClick={() => setForceDe(true)} className="underline text-[#b8893d] hover:text-[#8f6a2b]" data-testid="agb-show-german">Deutsche Fassung anzeigen</button>
              </p>
            </div>
          )}
          {forceDe && !siteLangIsDe && (
            <p className="mb-6 text-xs text-[#7a8a99]">
              Rechtlich maßgebliche deutsche Fassung.{' '}
              <button type="button" onClick={() => setForceDe(false)} className="underline text-[#b8893d]" data-testid="agb-show-translation">Übersetzung anzeigen</button>
            </p>
          )}
          {AGB_SECTIONS.map((sec) => (
            <div key={sec.title}>
              <h2 className={SECTION_HEADING}>{sec.title}</h2>
              {sec.paras.map((p) => (
                <p key={p.slice(0, 40)} className={`${BODY} mb-3`}>{p}</p>
              ))}
            </div>
          ))}

          {AGB_WIDERRUF && (<>
          <h2 className={SECTION_HEADING}>{AGB_WIDERRUF.title}</h2>
          <p className={`${BODY} mb-3 italic`}>{AGB_WIDERRUF.intro}</p>
          {AGB_WIDERRUF.blocks.map((b) => (
            <div key={b.heading}>
              <p className={`${BODY} mt-4 font-semibold text-[#1B3A5C]`}>{b.heading}</p>
              {b.paras.map((p) => (
                <p key={p.slice(0, 40)} className={`${BODY} mb-3`}>{p}</p>
              ))}
            </div>
          ))}
          <p className={`${BODY} mt-4 text-[11px] italic text-[#7a8a99]`}>— Ende der Widerrufsbelehrung —</p>
          </>)}
          {!agb && <p className={`${BODY} mt-6 italic`}>Inhalte werden geladen …</p>}
          <p className={`${BODY} mt-4 flex flex-wrap gap-x-6 gap-y-2`}>
            <a href={AGB_PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="agb-pdf-link" className="inline-flex items-center gap-2 text-[#b8893d] underline hover:text-[#8f6a2b]">
              <Download className="w-4 h-4" /> AGB als PDF herunterladen
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/widerrufsformular`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="agb-widerruf-pdf-link"
              className="inline-flex items-center gap-2 text-[#b8893d] underline hover:text-[#8f6a2b]"
            >
              <Download className="w-4 h-4" /> Muster-Widerrufsformular als PDF herunterladen
            </a>
          </p>

          <p className={`${BODY} mt-10 text-xs text-[#9aa5b1]`} data-testid="agb-stand">Stand: {AGB_STAND}.</p>
        </div>
      </section>
    </main>
    <Footer />
    <ChatWidget />
    <BackToTop />
  </div>
  );
};

export default AGB;
